import React, { useEffect, useState } from "react";
import { Loading } from "../components/others/Loading";
import { PageTitle } from "../../_jutemplate/layout/core";
import { useFetchDataForAll } from "../../hooks/useFetchDataForAll";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

// api
import { CropAPI } from "../../api/crop/cropAPI";
import { DiseaseInsectAPI } from "../../api/disease-insect/diseaseInsectAPI";
import { ProductAPI } from "../../api/product/productAPI";

const diseaseInsectApi = new DiseaseInsectAPI();
const cropApi = new CropAPI();
const productApi = new ProductAPI();

// Metric Card Component
const MetricCard: React.FC<{
  title: string;
  value: string | number;
  change: number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, change, subtitle, icon, color }) => {
  const isPositive = change >= 0;
  return (
    <div className="card shadow-sm border-0 h-100">
      <div className="card-body d-flex flex-column justify-content-between">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <span className="text-gray-600 fw-semibold fs-6">{title}</span>
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: color + "20",
            }}
          >
            {icon}
          </div>
        </div>
        <div>
          <div className="d-flex align-items-end gap-2 mb-2">
            <span className="fs-2x fw-bold text-gray-900">{value}</span>
            <span
              className={`badge badge-light-${
                isPositive ? "success" : "danger"
              } fs-7 mb-2`}
            >
              {isPositive ? "↑" : "↓"} {Math.abs(change)}%
            </span>
          </div>
          <span className="text-gray-500 fs-7">{subtitle}</span>
        </div>
      </div>
    </div>
  );
};

export const DashBoard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);

  // Data fetch custom hook
  const [cropData, cropDataLoading] = useFetchDataForAll(() =>
    cropApi.activeCropList(),
  );

  const [diseaseInsectData, diseaseInsectDataLoading] = useFetchDataForAll(() =>
    diseaseInsectApi.diseaseInsectActiveList(),
  );

  const [productData, productDataLoading] = useFetchDataForAll(() =>
    productApi.activeProductList(),
  );

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 1000);
  }, []);

  // Fake data for charts
  const revenueChartOptions: ApexOptions = {
    chart: {
      type: "area",
      height: 350,
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    dataLabels: { enabled: false },
    stroke: { curve: "smooth", width: 3 },
    colors: ["#12B76A", "#F79009"],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    xaxis: {
      categories: [
        "Jan 1",
        "Jan 5",
        "Jan 10",
        "Jan 15",
        "Jan 20",
        "Jan 25",
        "Jan 30",
      ],
      labels: { style: { colors: "#667085", fontSize: "12px" } },
    },
    yaxis: {
      labels: {
        style: { colors: "#667085", fontSize: "12px" },
        formatter: (val) => `৳${val.toFixed(0)}k`,
      },
    },
    grid: { borderColor: "#F3F4F6", strokeDashArray: 4 },
    legend: { show: true, position: "top", horizontalAlign: "right" },
    tooltip: {
      y: { formatter: (val) => `৳${val}k` },
    },
  };

  const revenueChartSeries = [
    {
      name: "This Month",
      data: [112, 95, 105, 153, 142, 158, 165],
    },
    {
      name: "Last Month",
      data: [85, 92, 88, 95, 98, 105, 110],
    },
  ];

  // Order Status Chart
  const orderStatusOptions: ApexOptions = {
    chart: {
      type: "bar",
      height: 350,
      toolbar: { show: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "55%",
        borderRadius: 8,
      },
    },
    dataLabels: { enabled: false },
    colors: ["#3B82F6", "#10B981", "#F59E0B", "#EF4444"],
    xaxis: {
      categories: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"],
      labels: { style: { colors: "#667085", fontSize: "12px" } },
    },
    yaxis: {
      labels: { style: { colors: "#667085", fontSize: "12px" } },
    },
    grid: { borderColor: "#F3F4F6", strokeDashArray: 4 },
    legend: { show: true, position: "top", horizontalAlign: "right" },
  };

  const orderStatusSeries = [
    { name: "Pending", data: [45, 52, 38, 45, 50, 42, 48] },
    { name: "Approved", data: [76, 85, 101, 98, 87, 105, 91] },
    { name: "Completed", data: [35, 41, 62, 42, 48, 52, 58] },
    { name: "Returned", data: [12, 8, 14, 10, 6, 9, 11] },
  ];

  // Dealer Performance Radial Chart
  const dealerPerformanceOptions: ApexOptions = {
    chart: {
      type: "radialBar",
      height: 280,
    },
    plotOptions: {
      radialBar: {
        hollow: { size: "65%" },
        dataLabels: {
          name: {
            fontSize: "16px",
            color: "#667085",
            offsetY: -10,
          },
          value: {
            fontSize: "32px",
            fontWeight: "bold",
            color: "#111827",
            offsetY: 5,
          },
        },
      },
    },
    colors: ["#12B76A"],
    labels: ["Active Dealers"],
  };

  const dealerPerformanceSeries = [68];

  // Geographic Distribution Donut Chart
  const geoDistributionOptions: ApexOptions = {
    chart: {
      type: "donut",
      height: 300,
    },
    labels: ["Dhaka", "Chattogram", "Rajshahi", "Khulna", "Others"],
    colors: ["#3B82F6", "#10B981", "#F59E0B", "#8B5CF6", "#EC4899"],
    legend: { position: "bottom" },
    dataLabels: { enabled: true },
    plotOptions: {
      pie: {
        donut: {
          size: "70%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Orders",
              fontSize: "14px",
              color: "#667085",
            },
          },
        },
      },
    },
  };

  const geoDistributionSeries = [2884, 1432, 562, 345, 201];

  // Best Selling Products
  const bestSellingProducts = [
    {
      id: "#B1009",
      name: "Hybrid Active Noise Cancelling",
      image: "🎧",
      sold: 2310,
      revenue: "৳124,839",
      rating: 4.5,
    },
    {
      id: "#B1001",
      name: "Gama G-Stock Shook Resistant",
      image: "⚡",
      sold: 1230,
      revenue: "৳89,692",
      rating: 4.8,
    },
    {
      id: "#B1003",
      name: "SAMSUNG Galaxy S25 Ultra",
      image: "📱",
      sold: 817,
      revenue: "৳77,654",
      rating: 4.7,
    },
    {
      id: "#B1002",
      name: "Xbox Wireless Gaming Controller",
      image: "🎮",
      sold: 645,
      revenue: "৳62,820",
      rating: 4.6,
    },
    {
      id: "#B1002",
      name: "Timex Men's Easy Reader",
      image: "⌚",
      sold: 572,
      revenue: "৳48,724",
      rating: 4.5,
    },
  ];

  return (
    <>
      <PageTitle>Dashboard</PageTitle>
      {loading ? (
        <Loading />
      ) : (
        <div className="row g-4">
          {/* Header Section with Date and Export Button */}
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="fw-bold mb-1">Dashboard</h2>
                <p className="text-gray-600 mb-0">
                  Jan 1, 2025 - Feb 1, 2025 · Last 30 days
                </p>
              </div>
              <button className="btn btn-light-primary btn-sm">
                <i className="bi bi-download me-2"></i>
                Export
              </button>
            </div>
          </div>

          {/* Metric Cards */}
          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="Total Dealers"
              value="342"
              change={12.5}
              subtitle="vs. 304 last period"
              color="#3B82F6"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 21H21M4 18H6C6.55228 18 7 17.5523 7 17V7C7 6.44772 6.55228 6 6 6H4C3.44772 6 3 6.44772 3 7V17C3 17.5523 3.44772 18 4 18ZM11 18H13C13.5523 18 14 17.5523 14 17V3C14 2.44772 13.5523 2 13 2H11C10.4477 2 10 2.44772 10 3V17C10 17.5523 10.4477 18 11 18ZM18 18H20C20.5523 18 21 17.5523 21 17V11C21 10.4477 20.5523 10 20 10H18C17.4477 10 17 10.4477 17 11V17C17 17.5523 17.4477 18 18 18Z"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="Orders"
              value="1,224"
              change={4.46}
              subtitle="vs. 1,172 last period"
              color="#10B981"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 3H5L5.4 5M7 13H17L21 5H5.4M7 13L5.4 5M7 13L4.707 15.293C4.077 15.923 4.523 17 5.414 17H17M17 17C15.895 17 15 17.895 15 19C15 20.105 15.895 21 17 21C18.105 21 19 20.105 19 19C19 17.895 18.105 17 17 17ZM9 19C9 20.105 8.105 21 7 21C5.895 21 5 20.105 5 19C5 17.895 5.895 17 7 17C8.105 17 9 17.895 9 19Z"
                    stroke="#10B981"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="Revenue"
              value="৳446.7K"
              change={8.24}
              subtitle="vs. ৳412.6k last period"
              color="#F59E0B"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6"
                    stroke="#F59E0B"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          <div className="col-xl-3 col-md-6">
            <MetricCard
              title="Active Users"
              value="6,225"
              change={5.44}
              subtitle="vs. 5,732 last period"
              color="#8B5CF6"
              icon={
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M17 21V19C17 17.9391 16.5786 16.9217 15.8284 16.1716C15.0783 15.4214 14.0609 15 13 15H5C3.93913 15 2.92172 15.4214 2.17157 16.1716C1.42143 16.9217 1 17.9391 1 19V21M23 21V19C22.9993 18.1137 22.7044 17.2528 22.1614 16.5523C21.6184 15.8519 20.8581 15.3516 20 15.13M16 3.13C16.8604 3.3503 17.623 3.8507 18.1676 4.55231C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45769C17.623 10.1593 16.8604 10.6597 16 10.88M13 7C13 9.20914 11.2091 11 9 11C6.79086 11 5 9.20914 5 7C5 4.79086 6.79086 3 9 3C11.2091 3 13 4.79086 13 7Z"
                    stroke="#8B5CF6"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
            />
          </div>

          {/* Total Revenue Chart */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Total Revenue</h3>
                <div className="card-toolbar">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fs-2x fw-bold text-gray-900">৳446.7K</span>
                    <span className="badge badge-light-success">↑ 8.24%</span>
                  </div>
                </div>
              </div>
              <div className="card-body pt-0">
                <ReactApexChart
                  options={revenueChartOptions}
                  series={revenueChartSeries}
                  type="area"
                  height={300}
                />
              </div>
            </div>
          </div>

          {/* Order Statistics */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Order Statistics</h3>
              </div>
              <div className="card-body pt-0">
                <div className="d-flex flex-column gap-4 mb-4">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "#3B82F615",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <span
                          className="fw-bold"
                          style={{ color: "#3B82F6", fontSize: "18px" }}
                        >
                          🔄
                        </span>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between">
                        <span className="text-gray-700 fw-semibold">
                          Pending
                        </span>
                        <span className="fw-bold">2,884</span>
                      </div>
                      <div className="progress mt-2" style={{ height: "6px" }}>
                        <div
                          className="progress-bar"
                          style={{ width: "45%", backgroundColor: "#3B82F6" }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "#10B98115",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <span
                          className="fw-bold"
                          style={{ color: "#10B981", fontSize: "18px" }}
                        >
                          ✅
                        </span>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between">
                        <span className="text-gray-700 fw-semibold">
                          Approved
                        </span>
                        <span className="fw-bold">1,432</span>
                      </div>
                      <div className="progress mt-2" style={{ height: "6px" }}>
                        <div
                          className="progress-bar"
                          style={{ width: "65%", backgroundColor: "#10B981" }}
                        ></div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded"
                      style={{
                        width: "40px",
                        height: "40px",
                        backgroundColor: "#F59E0B15",
                      }}
                    >
                      <div className="d-flex align-items-center justify-content-center h-100">
                        <span
                          className="fw-bold"
                          style={{ color: "#F59E0B", fontSize: "18px" }}
                        >
                          📦
                        </span>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <div className="d-flex justify-content-between">
                        <span className="text-gray-700 fw-semibold">
                          Completed
                        </span>
                        <span className="fw-bold">562</span>
                      </div>
                      <div className="progress mt-2" style={{ height: "6px" }}>
                        <div
                          className="progress-bar"
                          style={{ width: "35%", backgroundColor: "#F59E0B" }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Most Active Days Chart */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Weekly Order Activity</h3>
              </div>
              <div className="card-body pt-0">
                <ReactApexChart
                  options={orderStatusOptions}
                  series={orderStatusSeries}
                  type="bar"
                  height={300}
                />
              </div>
            </div>
          </div>

          {/* Dealer Performance Gauge */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Dealer Status</h3>
              </div>
              <div className="card-body pt-0 d-flex flex-column justify-content-center">
                <ReactApexChart
                  options={dealerPerformanceOptions}
                  series={dealerPerformanceSeries}
                  type="radialBar"
                  height={250}
                />
                <div className="text-center mt-4">
                  <button className="btn btn-light-success btn-sm">
                    Show details
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Best Selling Products */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Best Selling Products</h3>
                <div className="card-toolbar">
                  <button className="btn btn-sm btn-light">View All</button>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table table-row-bordered align-middle gs-4">
                    <thead>
                      <tr className="fw-bold text-gray-600 fs-7">
                        <th className="min-w-200px">NAME</th>
                        <th className="min-w-100px">SOLD</th>
                        <th className="min-w-100px">REVENUE</th>
                        <th className="min-w-100px">RATING</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bestSellingProducts.map((product, index) => (
                        <tr key={index}>
                          <td>
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="d-flex align-items-center justify-content-center rounded"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: "#F3F4F6",
                                  fontSize: "20px",
                                }}
                              >
                                {product.image}
                              </div>
                              <div>
                                <span className="text-gray-900 fw-bold d-block">
                                  {product.name}
                                </span>
                                <span className="text-gray-500 fs-7">
                                  {product.id}
                                </span>
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="text-gray-900 fw-semibold">
                              {product.sold.toLocaleString()} sold
                            </span>
                          </td>
                          <td>
                            <span
                              className="fw-bold"
                              style={{ color: "#12B76A" }}
                            >
                              {product.revenue}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex align-items-center gap-1">
                              <span className="text-warning">⭐</span>
                              <span className="text-gray-900 fw-semibold">
                                {product.rating}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Geographic Distribution */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0 h-100">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Orders by Division</h3>
              </div>
              <div className="card-body pt-0">
                <ReactApexChart
                  options={geoDistributionOptions}
                  series={geoDistributionSeries}
                  type="donut"
                  height={280}
                />
              </div>
            </div>
          </div>

          {/* Quick Stats with Real Data */}
          <div className="col-xl-4">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Quick Stats</h3>
              </div>
              <div className="card-body pt-0">
                <div className="d-flex flex-column gap-4">
                  <div
                    className="d-flex align-items-center justify-content-between p-4 rounded"
                    style={{ backgroundColor: "#F0FDF4" }}
                  >
                    <div>
                      <span className="text-gray-600 fs-7 d-block mb-1">
                        Total Products
                      </span>
                      <span className="text-gray-900 fs-2 fw-bold">
                        {productData?.data?.count || 0}
                      </span>
                    </div>
                    <div className="fs-2x">📦</div>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-between p-4 rounded"
                    style={{ backgroundColor: "#FEF3C7" }}
                  >
                    <div>
                      <span className="text-gray-600 fs-7 d-block mb-1">
                        Total Crops
                      </span>
                      <span className="text-gray-900 fs-2 fw-bold">
                        {cropData?.data?.count || 0}
                      </span>
                    </div>
                    <div className="fs-2x">🌾</div>
                  </div>

                  <div
                    className="d-flex align-items-center justify-content-between p-4 rounded"
                    style={{ backgroundColor: "#DBEAFE" }}
                  >
                    <div>
                      <span className="text-gray-600 fs-7 d-block mb-1">
                        Disease Reports
                      </span>
                      <span className="text-gray-900 fs-2 fw-bold">
                        {diseaseInsectData?.data?.count || 0}
                      </span>
                    </div>
                    <div className="fs-2x">🐛</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activities */}
          <div className="col-xl-8">
            <div className="card shadow-sm border-0">
              <div className="card-header border-0 pt-6">
                <h3 className="card-title fw-bold">Recent Activities</h3>
                <div className="card-toolbar">
                  <button className="btn btn-sm btn-light">View All</button>
                </div>
              </div>
              <div className="card-body pt-0">
                <div className="timeline">
                  <div className="timeline-item">
                    <div className="timeline-icon">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#E7F8F0",
                        }}
                      >
                        <i
                          className="bi bi-cart-check fs-5"
                          style={{ color: "#12B76A" }}
                        ></i>
                      </div>
                    </div>
                    <div className="timeline-content">
                      <span className="fs-6 text-gray-800 fw-semibold d-block mb-1">
                        New order received from Dhaka Division
                      </span>
                      <span className="fs-7 text-gray-500">
                        Order #12845 - 2 hours ago
                      </span>
                    </div>
                  </div>

                  <div className="timeline-item">
                    <div className="timeline-icon">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#FEF3C7",
                        }}
                      >
                        <i
                          className="bi bi-person-plus fs-5"
                          style={{ color: "#F59E0B" }}
                        ></i>
                      </div>
                    </div>
                    <div className="timeline-content">
                      <span className="fs-6 text-gray-800 fw-semibold d-block mb-1">
                        New dealer registration pending approval
                      </span>
                      <span className="fs-7 text-gray-500">
                        Dealer: Rahman Trading - 5 hours ago
                      </span>
                    </div>
                  </div>

                  <div className="timeline-item">
                    <div className="timeline-icon">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#DBEAFE",
                        }}
                      >
                        <i
                          className="bi bi-bug fs-5"
                          style={{ color: "#3B82F6" }}
                        ></i>
                      </div>
                    </div>
                    <div className="timeline-content">
                      <span className="fs-6 text-gray-800 fw-semibold d-block mb-1">
                        New disease detection report submitted
                      </span>
                      <span className="fs-7 text-gray-500">
                        Rice leaf blight - 8 hours ago
                      </span>
                    </div>
                  </div>

                  <div className="timeline-item">
                    <div className="timeline-icon">
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#F3E8FF",
                        }}
                      >
                        <i
                          className="bi bi-box-seam fs-5"
                          style={{ color: "#8B5CF6" }}
                        ></i>
                      </div>
                    </div>
                    <div className="timeline-content">
                      <span className="fs-6 text-gray-800 fw-semibold d-block mb-1">
                        Low stock alert for popular products
                      </span>
                      <span className="fs-7 text-gray-500">
                        5 products - Yesterday
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
